// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
/* Starter Custom Code
import {
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
// import * as React from "react"; // Store Custom Code
import { connect } from "react-redux";

/* Starter Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useContext } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import React, { useState, useMemo } from "react";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { linkStyles } from "@store/Themes/common/mixins/link";
import Modal from "@insite/mobius/Modal";
import UnitOfMesasureConversionTable from "@store/Library/Components/UnitOfMeasureConversionTable";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { buttonStyles } from "@store/Themes/common/mixins/button";
/* Store Custom Code - End */

export interface ProductUnitOfMeasureSelectProps extends Omit<OwnProps, keyof SelectPresentationProps> {}

export const useShowProductUnitOfMeasureSelect = () => {
    const { productSettings } = useSettingsCollection();
    const { product } = useContext(ProductContext);

    return !(
        !productSettings?.alternateUnitsOfMeasure ||
        !product.unitOfMeasures ||
        product.unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
    );
};
/* Starter Custom Code - End */

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
    // Starter Custom Code
    overridedStyles?: SelectPresentationProps;
    // Store Custom Code
    showConversionTable?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

/* Store Custom Code - Start */
export interface ProductUnitOfMeasureSelectStyles extends SelectPresentationProps {
    labelWrapper?: InjectableCss;
    conversionClickable?: ClickablePresentationProps;
    closeButton?: ButtonPresentationProps;
}

const buttonBaseCss = buttonStyles("solid");
/* Store Custom Code - End */

export const productUnitOfMeasureSelectStyles: ProductUnitOfMeasureSelectStyles /* Store Custom Code */ = {
    sizeVariant: "small",
    cssOverrides: {
        descriptionWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.descriptionWrapper}
        `,
        formField: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
        `,
        formInputWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formInputWrapper}
        `,
        inputSelect: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.inputSelect}
        `,
    },
    // Store Custom Code
    labelWrapper: {
        css: css`
            display: flex;
            gap: ${pxToRem(12)};
        `,
    },
    conversionClickable: {
        css: css`
            ${linkStyles("text.accent", "text", true, true)}
            font-weight: ${x => x.theme.design.typography.weight.base};
        `,
    },
    closeButton: {
        sizeVariant: "small",
        css: css`
            ${buttonBaseCss}
            min-width: 0;
        `,
    },
};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: { unitOfMeasures, properties },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    },
    disabled,
    productSettings,
    labelOverride,
    extendedStyles,
    overridedStyles, // Starter Custom Code
    showConversionTable, // Store Custom Code
    ...otherProps
}) => {
    const styles = useExpandProps(productUnitOfMeasureSelectStyles, extendedStyles, overridedStyles); // Starter Custom Code
    const [isModalOpen, setIsModalOpen] = useState(false); // Store Custom Code

    const sortedUnitOfMeasures = useMemo(() => {
        return unitOfMeasures?.slice().sort((a, b) => a.qtyPerBaseUnitOfMeasure - b.qtyPerBaseUnitOfMeasure);
    }, [unitOfMeasures]);

    if (!useShowProductUnitOfMeasureSelect() /* Starter Custom Code */) {
        return null;
    }

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event.target.value);
    };

    /* Store Custom Code - Start */
    const unitConversionTable = properties?.unitConversionTable;
    const baseLabel = labelOverride ?? translate("U/M");

    const label =
        showConversionTable && unitConversionTable ? (
            <StyledWrapper {...styles.labelWrapper}>
                {baseLabel}
                <Clickable onClick={() => setIsModalOpen(true)} {...styles.conversionClickable}>
                    {translate("Conversion Table")}
                </Clickable>
            </StyledWrapper>
        ) : (
            baseLabel
        );
    /* Store Custom Code - End */

    return (
        <>
            <Select
                label={label /* Store Custom Code */}
                value={unitOfMeasure}
                onChange={uomChangeHandler}
                disabled={disabled}
                data-test-selector="product_unitOfMeasureSelect"
                {...styles}
                {...otherProps}
            >
                {/* Starter Custom Code - next line - "?" */}
                {/* Store Custom Code - next line - "sortedUnitOfMeasures" */}
                {sortedUnitOfMeasures?.map(uom => (
                    <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                        {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                        {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""}
                    </option>
                ))}
            </Select>
            {/* Store Custom Code - Start */}
            {showConversionTable && unitConversionTable && (
                <Modal
                    headline={translate("Conversion Table")}
                    isOpen={isModalOpen}
                    handleClose={() => setIsModalOpen(false)}
                    sizeVariant="small"
                >
                    <UnitOfMesasureConversionTable unitConversionTable={unitConversionTable} />
                    <FormActions>
                        <Button onClick={() => setIsModalOpen(false)} {...styles.closeButton}>
                            {translate("Ok")}
                        </Button>
                    </FormActions>
                </Modal>
            )}
            {/* Store Custom Code - End */}
        </>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
